import axios from "axios";
import { ProspectInterface } from "./prospect";
import getUserInfo from "api/login/getUserInfo";

interface SProspectResponse {
  TotalResults: string;
  Data: ProspectInterface[];
}

/**
 * Fetches a paginated list of prospects from the appropriate API endpoint.
 * @param {number} page - The page number to fetch (1-based index).
 * @param {number} pageSize - The number of records to fetch per page.
 * @param {string} consultantName - Optional consultant name to filter prospects by.
 * @returns {Promise<{rows: ProspectInterface[]; total: number}>} - A promise resolving to the data formatted for MUI DataGrid.
 */
async function getProspects(
  page: number = 1,
  pageSize: number = 30,
  searchKeyword: string = "",
): Promise<{ rows: ProspectInterface[]; total: number }> {

  let employeeId: number = 0;
  let filterByConsultant = true; 

  try {
    const userInfo = await getUserInfo();
    employeeId = userInfo.data.EmployeeId;
    console.log("DEBUG getUserInfo Response:", userInfo.data.RolesV3);

    // Determine filterByConsultant based on RolesV3
    if (userInfo.data.RolesV3.includes("CANprospect_Admin")) {
      filterByConsultant = false;
      console.log("DEBUG filterByConsultant = false");
    }
  } catch (error) {
    console.error("DEBUG getUserInfo Error:", error);
    throw error;
  }

  const endpoint = filterByConsultant
    ? `/api/cantrackv3/SProspectForConsultant`
    : `/api/cantrackv3/SProspect`;

  try {

    const params: Record<string, any> = {
      id: 0, // Fetch all prospects
      searchKeyword: searchKeyword,
      page,
      take: pageSize,
    };

    if (filterByConsultant && employeeId > 0) {
      params.cId = employeeId; // Add consultantId if filtering by consultant
    }

    const { data } = await axios.get<SProspectResponse>(endpoint, {
      params,
    });

    const totalResults = parseInt(data.TotalResults || "0", 10);

    // Transform data to match the ProspectInterface, using defaults for null/undefined values
    const transformedRows = data.Data.map((prospect) => ({
      Id: prospect.Id || null,
      CompanyName: prospect.CompanyName || null,
      FirstName: prospect.FirstName || null,
      LastName: prospect.LastName || null,
      Phone: prospect.Phone || null,
      Email: prospect.Email || null,
      Address: prospect.Address || null,
      City: prospect.City || null,
      Province: prospect.Province || null,
      PostalCode: prospect.PostalCode || null,
      TitleDescription: prospect.TitleDescription || null,
      SicCodeDescription: prospect.SicCodeDescription || null,
      ConsultantId: prospect.ConsultantId || null,
      ProspectStatusId: prospect.ProspectStatusId || null,
      UserName: prospect.UserName || null,
      ProspectStatus: prospect.ProspectStatus || null,
      Website: prospect.Website || null,
      ProspectComment: prospect.ProspectComment || null,
      ConsultantName: prospect.ConsultantName || null,
    }));

    return {
      rows: transformedRows,
      total: totalResults,
    };
  } catch (error) {
    console.error(
      `DEBUG getProspects.ts: Error occurred while fetching prospects:`,
      error
    );
    throw error;
  }
}

export default getProspects;
